import { z } from 'zod';
import { WorkflowGraphInputSchema } from './workflow.js';
/** View level a managed Daily Brief workflow gathers for. UI copy says "robot"; the API says `device`. */
export const ManagedWorkflowLevelSchema = z.enum(['org', 'group', 'device']);
/**
 * Identity of one managed instance. `viewName` is REQUIRED: the template's
 * `nameTemplate` substitutes it. `orgId` inside the ref is always rewritten
 * by `ManagedWorkflowService.ensure` from its own org argument.
 */
export const DailyBriefManagedRefSchema = z.object({
    orgId: z.string().min(1),
    viewId: z.string().min(1),
    moduleNodeId: z.string().min(1),
    level: ManagedWorkflowLevelSchema,
    viewName: z.string().min(1).max(200),
});
/**
 * A managed template bundle. Superset of the human bundle
 * (`apps/api/.../workflow-bundle.service.ts` BundleSchema): same
 * `cortexBundleVersion` / `workflow` block, plus a `managed` block and a
 * typed graph. Lives in repo at
 * `packages/database/prisma/seeds/managed/<managedBy>.bundle.json`; the
 * loader (`@cortex/database/managed-templates`) is the validator. No
 * `workflow.name` (rendered from `nameTemplate`), no `agents` /
 * `modelConfigs` remap keys. (DEV-17471.)
 */
export const ManagedBundleSchema = z.object({
    cortexBundleVersion: z.literal(1),
    managed: z.object({
        managedBy: z.string().regex(/^[a-z][a-z0-9-]*$/),
        templateVersion: z.number().int().positive(),
        /**
         * `{{viewName}}`, `{{level}}` (org|group|device) and `{{levelLabel}}`
         * (Organization|Groups|Robots) are substituted from the managed ref.
         */
        nameTemplate: z.string().min(1).max(200),
        lockedNodeKeys: z.array(z.string()).min(1),
        /** Every key must also be in `lockedNodeKeys`, otherwise it is never enforced. */
        lockedFields: z.record(z.string(), z.array(z.string())),
        /**
         * Default schedule created with the instance; cadence is user-editable
         * afterwards. A cron whose minute field is exactly `0` is staggered per
         * workflow by `ensure`.
         */
        schedule: z.object({
            name: z.string().min(1),
            cron: z.string().min(1),
            timezone: z.string().nullable().default(null),
            preventOverlap: z.boolean().default(true),
        }),
    }),
    workflow: z.object({
        description: z.string().nullable().default(null),
        workflowType: z.string().min(1),
        inputSchema: z.record(z.string(), z.unknown()).nullable().default(null),
        outputSchema: z.record(z.string(), z.unknown()).nullable().default(null),
        outputMapping: z.string().nullable().default(null),
        dedupEnabled: z.boolean().default(false),
        signalKeyTemplate: z.string().nullable().default(null),
        graph: WorkflowGraphInputSchema,
    }),
});
/**
 * Placeholder served in place of a payload (`input`, `output`, `trace`,
 * tool arguments/results, event payloads…) of a run of a Formant-managed
 * workflow when the reader may not inspect managed runs. Such a run gathers
 * org-wide data as the org's admin identity, so its content is not for every
 * org member (DEV-17471).
 */
export const managedRunRedaction = { redacted: 'managed-workflow' };
/**
 * Keys of a run-event payload that only say WHERE the run is (which node,
 * how long, which kind of failure), never what it read or wrote. Everything
 * else on an event of a managed run — `run.started.input`,
 * `node.message.message`, `run.completed.output`, error text — is dropped.
 */
const managedRunEventStructuralKeys = [
    'nodeKey',
    'nodeType',
    'nodeExecutionId',
    'latencyMs',
    'kind',
    'requestedAt',
    'resolvedAt',
    'cancelledAt',
];
/**
 * The payload of a managed run's event as an uninspecting reader may see it:
 * the structural keys (scalar values only) plus the redaction marker. Used by
 * the api's event reads / SSE streams and by the webhook dispatcher, so the
 * three surfaces agree on what progress looks like without the data.
 */
export function redactManagedRunEventPayload(payload) {
    const out = { ...managedRunRedaction };
    if (!payload || typeof payload !== 'object' || Array.isArray(payload))
        return out;
    const source = payload;
    for (const key of managedRunEventStructuralKeys) {
        const value = source[key];
        if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
            out[key] = value;
        }
    }
    return out;
}
//# sourceMappingURL=managed-workflow.js.map